import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (V-L Phase 2 admin endpoints — legacy /admin/* envelope):
//
//   POST /admin/creator_application_list
//        { status?: "pending"|"approved"|"rejected", page, limit, sortyBy?, sortDirection? }
//        → { success, data: { docs: CreatorApplication[], totalDocs, ... }, message }
//
//   POST /admin/creator_application_get        { id }
//        → { success, data: CreatorApplication }
//
//   POST /admin/creator_application_approve    { id }
//        → { success, data: CreatorApplication } (status now "approved", coachId set)
//
//   POST /admin/creator_application_reject     { id, reason? }
//        → { success, data: CreatorApplication } (status now "rejected")
//
// Approval creates a Coach record + sets user.coach_id + fires a
// usernotification. Rejection just stamps status + reason and notifies
// the user. Both are idempotent at the service layer.
// ───────────────────────────────────────────────────────────────────────

export type CreatorApplicationStatus = "pending" | "approved" | "rejected"

export type CreatorApplication = {
  id: string
  status: CreatorApplicationStatus
  bio: string
  topics: string[]
  sampleLinks: string[]
  rejectionReason: string
  submittedAt: string
  reviewedAt: string | null
  reviewerId: string | null
  coachId: string | null
  applicant: {
    id: string
    name: string
    email: string
    avatarUrl: string | null
  } | null
}

export type ApplicationListParams = {
  status?: CreatorApplicationStatus
  page: number
  limit: number
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type ApplicationListResponse = {
  docs: CreatorApplication[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

const listKey = (p: ApplicationListParams) =>
  ["admin", "creator-applications", "list", p] as const

export function useCreatorApplications(params: ApplicationListParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: async (): Promise<ApplicationListResponse> => {
      const res = await api.post<{
        success: boolean
        data: ApplicationListResponse
        message: string
      }>("/admin/creator_application_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages:
          typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useApproveApplication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{
        success: boolean
        data: CreatorApplication
        message: string
      }>("/admin/creator_application_approve", { id })
      return res.data
    },
    onSuccess: () => {
      // Both the applications list AND the coaches list change
      // (a new Coach record was created). Invalidate both namespaces.
      qc.invalidateQueries({ queryKey: ["admin", "creator-applications"] })
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] })
    },
  })
}

export function useRejectApplication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { id: string; reason?: string }) => {
      const res = await api.post<{
        success: boolean
        data: CreatorApplication
        message: string
      }>("/admin/creator_application_reject", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "creator-applications"] })
    },
  })
}
